import React from "react";
import { ExternalLink, Linkedin, Search } from "lucide-react";
import MarkThumbnail from "./MarkThumbnail";
import GoodsAndServices from "./GoodsAndServices";
import { Chip, StatusBadge } from "../ui/Badge";
import {
  EM_DASH,
  formatDate,
  formatOwners,
  officeLabel,
} from "../../lib/format";
import { contactSearchLinks, splitGoodsAndServices } from "../../lib/licensing";

/**
 * The results view for the Potential Licensing page. Deliberately not the
 * table/list/grid trio the clearance search uses — this answers a
 * different question ("who could I license this from, and in which of
 * their classes"), so it leads with the two things that matter for that:
 * which of the owner's classes actually overlap with what was searched,
 * and how to go start a conversation with them.
 */
export default function LicensingResultsList({
  rows,
  selected,
  onToggleRow,
  onOpenImage,
  onOpenDetail,
  isDetailLoading,
  searchedClasses,
}) {
  return (
    <div className="divide-y divide-ink-100 border-x border-ink-200 bg-white">
      {rows.map((row) => {
        const isSelected = selected.has(row.trademarkId);
        const detailLoading = isDetailLoading?.(row);
        const { matched, other } = splitGoodsAndServices(
          row.goodsAndServices,
          searchedClasses,
        );
        const owners = row.detail?.owners?.length
          ? row.detail.owners
          : row.applicantName
            ? [{ name: row.applicantName, address: "" }]
            : [];

        return (
          <article key={row.trademarkId} className="flex gap-4 p-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleRow(row.trademarkId)}
              aria-label={`Select ${row.wordMark || "this record"}`}
              className="mt-1.5 h-4 w-4 shrink-0 cursor-pointer rounded border-ink-300 text-ink-900 focus:ring-seal-500/40"
            />

            <MarkThumbnail
              wordMark={row.wordMark}
              imageUrl={row.imageUrl}
              size="lg"
              onClick={() =>
                onOpenImage({ url: row.imageUrl, wordMark: row.wordMark })
              }
            />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenDetail(row)}
                  className="font-display text-base font-semibold text-ink-900 hover:text-[#8f5eeb]-600"
                >
                  {row.wordMark || EM_DASH}
                </button>
                <StatusBadge status={row.status} />
                {row.jurisdiction && (
                  <Chip>{officeLabel(row.jurisdiction)}</Chip>
                )}
                {row.registrationDate && (
                  <Chip>Registered {formatDate(row.registrationDate)}</Chip>
                )}
              </div>

              {/* Matching classes first — this is *why* the row is here. */}
              <div className="mt-3">
                {matched.length > 0 ? (
                  <>
                    <p className="text-micro font-semibold uppercase tracking-wide text-[#8f5eeb]-600">
                      Matches {matched.length} of your searched class
                      {matched.length === 1 ? "" : "es"}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {matched.map((g) => (
                        <span
                          key={g.classNumber}
                          className="rounded border border-[#8f5eeb]/30 bg-[#8f5eeb]-100 px-2 py-0.5 text-micro font-semibold text-[#8f5eeb]-700"
                        >
                          Class {g.classNumber}
                        </span>
                      ))}
                    </div>
                    <GoodsAndServices
                      items={matched}
                      loading={detailLoading}
                      lines={2}
                      expandable
                      className="mt-1.5 text-sm text-ink-800"
                    />
                  </>
                ) : searchedClasses.length > 0 ? (
                  <p className="text-sm text-ink-500">
                    No direct overlap with the classes you searched — showing
                    its full coverage below.
                  </p>
                ) : null}

                {other.length > 0 && (
                  <GoodsAndServices
                    items={other}
                    loading={detailLoading}
                    lines={matched.length ? 1 : 3}
                    expandable
                    emptyLabel=""
                    className={`text-sm text-ink-500 ${matched.length ? "mt-2" : ""}`}
                  />
                )}
              </div>

              {/* Owner + outreach shortcuts */}
              <div className="mt-3 flex flex-wrap items-start justify-between gap-3 border-t border-ink-100 pt-3">
                <div className="min-w-0">
                  <p className="text-micro font-semibold uppercase tracking-wide text-ink-400">
                    Owner
                  </p>
                  {owners.length ? (
                    owners.map((owner, i) => (
                      <p key={i} className="text-sm text-ink-800">
                        {owner.name}
                        {owner.address && (
                          <span className="text-ink-500">
                            {" "}
                            — {owner.address}
                          </span>
                        )}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-ink-400">
                      {detailLoading ? "Loading…" : "Not published"}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                  {/* ✅ NOW: Generates links directly from the mark name (row.wordMark) */}
                  {(() => {
                    const links = contactSearchLinks(row.wordMark);
                    if (!links) return null;
                    return (
                      <>
                        <a
                          href={links.web}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Search for "${row.wordMark}" official website`}
                          className="inline-flex h-8 items-center gap-1.5 rounded border border-ink-200 bg-white px-2.5 text-xs font-semibold text-ink-700 hover:border-ink-300 hover:bg-ink-100/60"
                        >
                          <Search className="h-3.5 w-3.5" aria-hidden="true" />
                          Find website
                        </a>
                        <a
                          href={links.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Search for "${row.wordMark}" on LinkedIn`}
                          className="inline-flex h-8 items-center gap-1.5 rounded border border-ink-200 bg-white px-2.5 text-xs font-semibold text-ink-700 hover:border-ink-300 hover:bg-ink-100/60"
                        >
                          <Linkedin
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                          LinkedIn
                        </a>
                      </>
                    );
                  })()}

                  <button
                    type="button"
                    onClick={() => onOpenDetail(row)}
                    className="inline-flex h-8 items-center gap-1.5 rounded bg-ink-900 px-2.5 text-xs font-semibold text-white hover:bg-ink-800"
                  >
                    Full record
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
